import { useEffect, useRef, useState } from 'react';

import { buildChipSelection, chipsAreDefault, type ChipStates } from '@/components/PhraseChips';
import { useYoutubeCaptions } from '@/hooks/useYoutubeCaptions';
import { apiClient, type PhraseChip, type PhraseTokenDto, type YoutubeCaption, type YoutubeSearchItem } from '@/services';
import { BufferedLiveSearchPager, isAbortError } from './liveSearchPaging';

const noChipStates: ChipStates = new Map();
const noPhraseTokens: readonly PhraseTokenDto[] = [];

interface UseCardVideosOptions {
  enabled?: boolean;
  pageSize?: number;
  /** The card's chips, as the card reported them. */
  initialChips?: readonly PhraseChip[];
  /** The card's phrase as the matcher sees it; the corpus search takes it back with the chips. */
  phraseTokens?: readonly PhraseTokenDto[];
  /**
   * Per-chip state, keyed by lemma. Anything other than what the card stored — a word released, a
   * part of speech relaxed, a chip switched off — widens the search.
   */
  chipStates?: ChipStates;
}

interface UseCardVideosResult {
  items: YoutubeSearchItem[];
  /**
   * The chips for this card — lemma and the part of speech the card gave it, as the matcher sees
   * its phrase.
   */
  chips: PhraseChip[];
  /** Whether every chip still stands for what the card stored. Drives copy only, not the fetch. */
  atDefault: boolean;
  activeItem: YoutubeSearchItem | undefined;
  activeIndex: number;
  hasMore: boolean;
  canMoveNext: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string;
  loadMoreError: string;
  captions: YoutubeCaption[];
  captionsLoading: boolean;
  captionsError: string;
  goToNext: () => Promise<void>;
  goToPrevious: () => void;
  goToIndex: (index: number) => void;
  retry: () => void;
}

/**
 * Feeds the card's Videos panel, the way {@link useCardFeed} feeds its Feed panel: it always runs a
 * live subtitle search for the card's phrase — the full phrase by default, or whatever it is relaxed
 * to as chips are released, widened, or switched off. On top of that it keeps the queue position,
 * because this panel plays one result at a time.
 */
export function useCardVideos(
  meaningId: string | undefined,
  {
    enabled = true,
    pageSize = 20,
    initialChips = [],
    phraseTokens = noPhraseTokens,
    chipStates = noChipStates,
  }: UseCardVideosOptions = {}
): UseCardVideosResult {
  const [items, setItems] = useState<YoutubeSearchItem[]>([]);
  const [chips, setChips] = useState<PhraseChip[]>(() => [...initialChips]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [settledKey, setSettledKey] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [loadedFor, setLoadedFor] = useState(meaningId);
  const initialChipsKey = initialChips.map((chip) => `${chip.lemma}:${chip.pos}`).join('|');
  const [loadedInitialChipsKey, setLoadedInitialChipsKey] = useState(initialChipsKey);
  const retry = () => setRetryCount((count) => count + 1);
  const requestControllerRef = useRef<AbortController | null>(null);
  const livePagerRef = useRef<BufferedLiveSearchPager<YoutubeSearchItem> | null>(null);
  const itemsRef = useRef<YoutubeSearchItem[]>(items);
  itemsRef.current = items;

  // Dropped during render rather than in an effect: chips belong to one card, and a render that
  // still held the previous card's words would resolve a selection this card was never asked about.
  if (loadedFor !== meaningId || loadedInitialChipsKey !== initialChipsKey) {
    setLoadedFor(meaningId);
    setLoadedInitialChipsKey(initialChipsKey);
    setChips([...initialChips]);
  }

  const atDefault = chipsAreDefault(chips, chipStates);
  const selection = buildChipSelection(chips, chipStates);
  // Effects key off the resolved selection, not the identity of the map that carries it, so a
  // re-render that reaches the same selection does not refetch.
  const queryKey = `search:${selection.words.join(' ')}|${selection.anyWords.join(' ')}|${selection.posOverrides.join(' ')}`;
  const requestKey = `${meaningId ?? ''}|${queryKey}|${retryCount}`;
  // Derived rather than held in state, so opening the panel already reads as loading in the render
  // that opens it. A flag only an effect can raise leaves one painted frame claiming the card has
  // no videos.
  const loading = enabled && settledKey !== requestKey;

  const activeItem = items[activeIndex];
  const {
    captions,
    loading: captionsLoading,
    error: captionsError,
  } = useYoutubeCaptions(activeItem?.videoId);
  const canMoveNext = activeIndex < items.length - 1 || hasMore;

  const fetchPage = (cursor: string | null, limit = pageSize, signal?: AbortSignal) =>
    meaningId
      ? apiClient.searchVideos(phraseTokens, selection, cursor, limit, signal)
      : Promise.reject(new Error('no meaning id'));

  useEffect(() => {
    requestControllerRef.current?.abort();
    livePagerRef.current = null;

    if (!enabled) {
      // Closing throws the results away, so reopening must not read as already settled: the panel
      // would show its empty state for a frame before the refetch it is about to start.
      setSettledKey('');
      return;
    }

    const controller = new AbortController();
    requestControllerRef.current = controller;
    const isCurrent = () => requestControllerRef.current === controller && !controller.signal.aborted;

    setError('');
    setLoadMoreError('');
    setItems([]);
    setActiveIndex(0);
    setHasMore(false);

    const pager = new BufferedLiveSearchPager<YoutubeSearchItem>({
      pageSize,
      getKey: (item) => String(item.sentenceId),
      fetchWindow: async (cursor, limit) => {
        return fetchPage(cursor, limit, controller.signal);
      },
    });
    livePagerRef.current = pager;

    pager.loadFirst((chunk) => {
      if (!isCurrent()) return;
      setItems((current) => [...current, ...chunk]);
      setSettledKey(requestKey);
    })
      .then((page) => {
        if (!isCurrent()) return;
        setHasMore(page.hasMore);
        if (page.hasMore) {
          pager.prefetch(
            page.items.map((item) => String(item.sentenceId)),
            (prefetched) => {
              if (isCurrent()) setHasMore(prefetched.items.length > 0 || prefetched.hasMore);
            }
          );
        }
      })
      .catch((caught) => {
        if (isCurrent() && !isAbortError(caught)) {
          setError('Unable to load videos. Please try again.');
        }
      })
      .finally(() => {
        if (isCurrent()) setSettledKey(requestKey);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meaningId, enabled, pageSize, queryKey, retryCount]);

  /** Walks forward, pulling the next page only once the loaded ones are used up. */
  const goToNext = async () => {
    if (activeIndex < items.length - 1) {
      setActiveIndex((index) => index + 1);
      setLoadMoreError('');
      return;
    }

    if (!hasMore || loadingMore) return;

    setLoadingMore(true);
    setLoadMoreError('');
    try {
      const controller = requestControllerRef.current;
      if (!controller || controller.signal.aborted) return;
      const nextIndex = itemsRef.current.length;

      const pager = livePagerRef.current;
      if (!pager) return;
      const page = await pager.takeNext(itemsRef.current.map((item) => String(item.sentenceId)));
      if (requestControllerRef.current !== controller || !page) return;

      setItems((current) => [...current, ...page.items]);
      setHasMore(page.hasMore);
      if (page.items.length > 0) setActiveIndex(nextIndex);
      if (page.hasMore) {
        const knownKeys = [...itemsRef.current, ...page.items].map((item) => String(item.sentenceId));
        pager.prefetch(knownKeys, (prefetched) => {
          if (requestControllerRef.current === controller) {
            setHasMore(prefetched.items.length > 0 || prefetched.hasMore);
          }
        });
      }
    } catch (caught) {
      if (!isAbortError(caught)) {
        setLoadMoreError('Unable to load the next result. Please try again.');
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const goToPrevious = () => {
    setActiveIndex((index) => Math.max(0, index - 1));
    setLoadMoreError('');
  };

  const goToIndex = (index: number) => {
    if (index < 0 || index >= items.length) return;
    setActiveIndex(index);
    setLoadMoreError('');
  };

  return {
    items,
    chips,
    atDefault,
    activeItem,
    activeIndex,
    hasMore,
    canMoveNext,
    loading,
    loadingMore,
    error,
    loadMoreError,
    captions,
    captionsLoading,
    captionsError,
    goToNext,
    goToPrevious,
    goToIndex,
    retry,
  };
}
